import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ACTIONS, EVENTS, useJoyride } from 'react-joyride';
import { offset } from '@floating-ui/react';
import { COLORS } from '@/styles/colors';
import {
  ONBOARDING_GUIDE_FINISHED_STORAGE_KEY,
  ONBOARDING_GUIDE_LAST_STEP_INDEX_STORAGE_KEY,
  ONBOARDING_GUIDE_STEPS,
  ONBOARDING_GUIDE_STEP_ID,
  OnboardingGuideStepIdType,
} from '@/context/OnboardingGuideContext/const';
import { THEME } from '@/styles/theme';
import { localStorageService } from '@/helpers/storageHelpers';
import { getTransactionsPath } from '@/bundle/_OrgAdmin/path/path';
import { useUser } from '../userContext';
import { isActiveRoute } from '@/bundle/Sidebar/helpers/helpers';
import noop from 'lodash/noop';

type OnboardingGuideContextType = {
  showStep: (stepId: OnboardingGuideStepIdType) => void;
  initGuide: () => void;
  finishGuide: () => void;
  resetGuide: () => void;
  closeGuideStep: () => void;
  isInitializedFirstStep: boolean;
  isFinished: boolean;
};

const OnboardingGuideContextProvider = createContext<OnboardingGuideContextType>({
  showStep: noop,
  initGuide: noop,
  finishGuide: noop,
  resetGuide: noop,
  closeGuideStep: noop,
  isInitializedFirstStep: false,
  isFinished: false,
});

export const OnboardingGuideProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const { isMasterAdminRole, isTrialAdmin } = useUser();
  const [stepIndex, setStepIndex] = useState<number>(() => {
    return localStorageService.get(ONBOARDING_GUIDE_LAST_STEP_INDEX_STORAGE_KEY) || 0;
  });
  const [isInProgress, setIsInProgress] = useState(false);
  const [isInitializedFirstStep, setIsInitializedFirstStep] = useState(false);
  const [isFinished, setIsFinished] = useState(() => {
    return !!localStorageService.get(ONBOARDING_GUIDE_FINISHED_STORAGE_KEY);
  });
  const isTrialModeMasterAdmin = isMasterAdminRole && isTrialAdmin;

  const getRunState = () => {
    if (!isTrialModeMasterAdmin || isFinished) return false;

    return !(isActiveRoute(location.pathname, [getTransactionsPath()]) && location.state?.showTrialModeModal);
  };

  const run = getRunState();

  const { Tour, controls, on } = useJoyride({
    steps: ONBOARDING_GUIDE_STEPS,
    stepIndex,
    continuous: false,
    run,
    floatingOptions: {
      middleware: [offset(-10)],
    },
    options: {
      arrowColor: COLORS.black,
      overlayClickAction: false,
      overlayColor: 'rgba(102, 111, 120, 0.50)',
      spotlightRadius: 12,
      showProgress: false,
      zIndex: THEME.onboardingGuide.zIndex,
    },
    locale: {
      last: 'Finish',
      skip: 'Close',
    },
  });

  const showStep = (stepId: OnboardingGuideStepIdType) => {
    const stepIndex = ONBOARDING_GUIDE_STEPS.findIndex(({ id }) => id === stepId);

    setStepIndex(stepIndex);
  };

  const initGuide = () => {
    if (isFinished || isInProgress) return;

    setStepIndex(0);
    setIsInProgress(true);
    setIsFinished(false);

    controls.start();
  };

  const finishGuide = () => {
    localStorageService.set(ONBOARDING_GUIDE_FINISHED_STORAGE_KEY, true);
    localStorageService.set(ONBOARDING_GUIDE_LAST_STEP_INDEX_STORAGE_KEY, 0);

    setIsInProgress(false);
    setIsInitializedFirstStep(false);
    setIsFinished(true);
    setStepIndex(0);

    controls.stop();
  };

  const resetGuide = () => {
    controls.reset();
    localStorageService.remove(ONBOARDING_GUIDE_FINISHED_STORAGE_KEY);

    setIsFinished(false);
    setIsInProgress(true);
    setIsInitializedFirstStep(false);
    setStepIndex(0);

    controls.start();
  };

  const closeGuideStep = () => {
    if (isFinished) return;

    controls.close();
  };

  useEffect(() => {
    if (isInitializedFirstStep) return;

    if (stepIndex > 0) {
      setIsInitializedFirstStep(true);
    }
  }, [stepIndex, isInitializedFirstStep]);

  useEffect(() => {
    return on(EVENTS.TOOLTIP, () => {
      localStorageService.set(ONBOARDING_GUIDE_LAST_STEP_INDEX_STORAGE_KEY, stepIndex);
    });
  }, [on, stepIndex]);

  useEffect(() => {
    return on(EVENTS.STEP_AFTER, (data) => {
      const { action, step } = data;
      const currentStepId = step.id;

      if (!currentStepId) return;

      setIsInProgress(true);

      if (action === ACTIONS.CLOSE) {
        // This steps starts manually with "showStep" from the related components
        if (currentStepId === ONBOARDING_GUIDE_STEP_ID.DEPOSIT_ACCOUNTS) {
          setIsInitializedFirstStep(true);
        }

        if (
          currentStepId === ONBOARDING_GUIDE_STEP_ID.CREATE_TRANSACTION ||
          currentStepId === ONBOARDING_GUIDE_STEP_ID.CREATE_WIRE
        ) {
          return;
        }

        // Last step - finish the flow
        if (currentStepId === ONBOARDING_GUIDE_STEP_ID.ASSIGN_RECIPIENT) {
          finishGuide();

          return;
        }

        setStepIndex((index) => index + 1);

        return;
      }

      if (action === ACTIONS.PREV) {
        setStepIndex((index) => index - 1);

        if (step.data?.previousPath) {
          if (currentStepId === ONBOARDING_GUIDE_STEP_ID.ASSIGN_RECIPIENT) {
            navigate(step.data?.previousPath(params.transactionId));

            return;
          }

          navigate(step.data?.previousPath());
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, controls, params]);

  useEffect(() => {
    return on(EVENTS.TOUR_END, () => {
      finishGuide();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on]);

  return (
    <OnboardingGuideContextProvider.Provider
      value={{
        showStep,
        initGuide,
        finishGuide,
        resetGuide,
        closeGuideStep,
        isInitializedFirstStep,
        isFinished,
      }}
    >
      {children}
      {Tour}
    </OnboardingGuideContextProvider.Provider>
  );
};

export const useOnboardingGuide = () => {
  return useContext(OnboardingGuideContextProvider);
};
